import { CreateSimulationGroupRequest, Simulation, SimulationGroup } from '@thive/fddm-types';

import { logger } from '@/modules/common/logging/logger';
import { ISimulationService } from '@/modules/simulation/service/ISimulationService';
import { DeepPartial } from '@/modules/simulation/useSimulationGroupStore';

export class SimulationServiceLogDecorator implements ISimulationService {
  private readonly decoree: ISimulationService;

  constructor(decoree: ISimulationService) {
    this.decoree = decoree;
  }

  useDraft(): CreateSimulationGroupRequest {
    const start = this.logStart('useDraft');
    const draft = this.decoree.useDraft();
    this.logEnd('useDraft', start);
    return draft;
  }

  getDraft(): CreateSimulationGroupRequest {
    const start = this.logStart('getDraft');
    const draft = this.decoree.getDraft();
    this.logEnd('getDraft', start);
    return draft;
  }

  createDraft(name: string): CreateSimulationGroupRequest {
    const start = this.logStart('createDraft');
    const draft = this.decoree.createDraft(name);
    this.logEnd('createDraft', start);
    return draft;
  }

  createDraftFromRecord(record: SimulationGroup, name: string): CreateSimulationGroupRequest {
    const start = this.logStart('createDraftFromRecord');
    const draft = this.decoree.createDraftFromRecord(record, name);
    this.logEnd('createDraftFromRecord', start);
    return draft;
  }

  updateDraft(value: DeepPartial<CreateSimulationGroupRequest>): void {
    const start = this.logStart('updateDraft');
    this.decoree.updateDraft(value);
    this.logEnd('updateDraft', start);
  }

  getRecommendedSimulationTime(): number {
    const start = this.logStart('getRecommendedSimulationTime');
    const recommendedSimulationTime = this.decoree.getRecommendedSimulationTime();
    this.logEnd('getRecommendedSimulationTime', start);
    return recommendedSimulationTime;
  }

  getMaxCapacity(vehicleType: string): number {
    const start = this.logStart('getMaxCapacity');
    const maxCapacity = this.decoree.getMaxCapacity(vehicleType);
    this.logEnd('getMaxCapacity', start);
    return maxCapacity;
  }

  async getSimulationGroups(): Promise<SimulationGroup[]> {
    const start = this.logStart('getSimulationGroups');
    const simulationGroups = await this.decoree.getSimulationGroups();
    this.logEnd('getSimulationGroups', start);
    return simulationGroups;
  }

  async getSimulationGroup(simulationGroupId: string): Promise<SimulationGroup> {
    const start = this.logStart('getSimulationGroup');
    const simulationGroup = await this.decoree.getSimulationGroup(simulationGroupId);
    this.logEnd('getSimulationGroup', start);
    return simulationGroup;
  }

  async getSimulationsInGroup(simulationGroupId: string): Promise<Simulation[]> {
    const start = this.logStart('getSimulationsInGroup');
    const simulations = await this.decoree.getSimulationsInGroup(simulationGroupId);
    this.logEnd('getSimulationsInGroup', start);
    return simulations;
  }

  async createSimulationGroup(): Promise<SimulationGroup | null> {
    const start = this.logStart('createSimulationGroup');
    const simulationGroup = await this.decoree.createSimulationGroup();
    this.logEnd('createSimulationGroup', start);
    return simulationGroup;
  }

  async reRunSimulationGroup(simulationGroupId: string): Promise<SimulationGroup | null> {
    const start = this.logStart('reRunSimulationGroup');
    const simulationGroup = await this.decoree.reRunSimulationGroup(simulationGroupId);
    this.logEnd('reRunSimulationGroup', start);
    return simulationGroup;
  }

  async renameSimulationGroup(simulationGroupId: string, name: string): Promise<void> {
    const start = this.logStart('renameSimulationGroup');
    await this.decoree.renameSimulationGroup(simulationGroupId, name);
    this.logEnd('renameSimulationGroup', start);
  }

  async deleteSimulationGroup(simulationGroupId: string): Promise<void> {
    const start = this.logStart('deleteSimulationGroup');
    await this.decoree.deleteSimulationGroup(simulationGroupId);
    this.logEnd('deleteSimulationGroup', start);
  }

  async abortSimulationGroup(simulationGroupId: string): Promise<void> {
    const start = this.logStart('abortSimulationGroup');
    await this.decoree.abortSimulationGroup(simulationGroupId);
    this.logEnd('abortSimulationGroup', start);
  }

  async downloadSimulationFiles(simulationRunId: string, simulationName: string): Promise<void> {
    const start = this.logStart('downloadSimulationFiles');
    await this.decoree.downloadSimulationFiles(simulationRunId, simulationName);
    this.logEnd('downloadSimulationFiles', start);
  }

  async downloadFmsConfig(): Promise<void> {
    const start = this.logStart('downloadFmsConfig');
    await this.decoree.downloadFmsConfig();
    this.logEnd('downloadFmsConfig', start);
  }

  getCustomOrderProfileUrl(): string | undefined {
    const start = this.logStart('getCustomOrderProfileUrl');
    const customOrderProfileUrl = this.decoree.getCustomOrderProfileUrl();
    this.logEnd('getCustomOrderProfileUrl', start);
    return customOrderProfileUrl;
  }

  getCustomFmsConfigUrl(): string | undefined {
    const start = this.logStart('getCustomFmsConfigUrl');
    const customFmsConfigUrl = this.decoree.getCustomFmsConfigUrl();
    this.logEnd('getCustomFmsConfigUrl', start);
    return customFmsConfigUrl;
  }

  async getSimulationFiles(): Promise<{
    fmsConfigFiles: object[];
    fmsConfigFileNames: string[];
    orderProfileFiles: Blob[];
    orderProfileFileNames: string[];
  }> {
    const start = this.logStart('getSimulationFiles');
    const simulationFiles = await this.decoree.getSimulationFiles();
    this.logEnd('getSimulationFiles', start);
    return simulationFiles;
  }

  //
  // private utility methods
  //

  private logStart(methodName: string): number {
    const start: number = performance.now();
    logger.info(`Start -> ${methodName}`);
    return start;
  }

  private logEnd(methodName: string, start: number): void {
    logger.info(`End -> ${methodName} (${(performance.now() - start).toFixed(2)}ms)`);
  }
}
